import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService, type LocationInfo } from '../../services/supabaseLocationService';
import { ChartRendererSupabase } from './ChartRendererSupabase';

interface TransportationBurdenSectionProps {
  location: string;
  chartView?: 'burden' | 'cost';
  onSummaryCalculated?: (summary: {
    locationBurdenPct: number | null;
    californiaBurdenPct: number | null;
    locationMonthlyCost: number | null;
    californiaMonthlyCost: number | null;
    year: number | null;
  }) => void;
}

interface BurdenMetrics {
  avgBurdenPct: number | null;
  avgMonthlyCost: number | null;
  year: number | null;
  tractCount: number;
}

interface BurdenYearPoint {
  year: number;
  avgBurdenPct: number | null;
  avgMonthlyCost: number | null;
  tractCount: number;
}

interface BurdenRow {
  year: number;
  burden: number;
  total_cost: number;
  location_name: string | null;
  county: string | null;
  state: string | null;
  geofips: string | number | null;
  allocation_weight?: number;
}

interface TractPlaceCrosswalkRow {
  trct: string | number;
  stplc: string | number;
  weight: number;
}

/**
 * Transportation burden data is produced by python/transportation_burden.py
 * (and related equity indicators in python/clean_equity_indicators.py).
 * Table: transportation_burden (tract-level: burden = cost / monthly_income, total_cost, monthly_vmt, etc.)
 */
export const TransportationBurdenSection: React.FC<TransportationBurdenSectionProps> = ({ location, chartView = 'burden', onSummaryCalculated }) => {
  const [metrics, setMetrics] = useState<BurdenMetrics>({
    avgBurdenPct: null,
    avgMonthlyCost: null,
    year: null,
    tractCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [series, setSeries] = useState<BurdenYearPoint[]>([]);
  const [stateSeries, setStateSeries] = useState<BurdenYearPoint[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      setMetrics({ avgBurdenPct: null, avgMonthlyCost: null, year: null, tractCount: 0 });
      setSeries([]);
      setStateSeries([]);

      try {
        // Your Supabase upload from db.transportation_burden.csv created a table
        // with columns: geofips, state, geography_type, year, month, monthly_vmt,
        // gas_price, fuel_economy, total_cost, burden.
        //
        // Supabase shows tables as `public.<table_name>` in the UI. In this
        // project, the canonical table created from db.transportation_burden.csv
        // is named `transportation_burden`. Since there is no location_name
        // column, we compute summary metrics for the entire table (typically
        // tract-level) for the latest year available.

        const candidateTables = ['transportation_burden'];
        let rows: BurdenRow[] | null = null;
        let lastError: string | null = null;

        await supabaseLocationService.initialize();
        const locationInfo = supabaseLocationService.getAvailableLocations().find((loc) => loc.name === location) ?? null;

        const fetchBurdenRows = async (tableName: string, countyName?: string | null): Promise<BurdenRow[]> => {
          const allRows: BurdenRow[] = [];
          const pageSize = 10000;
          let from = 0;

          while (true) {
            let query = supabaseClient
              .from(tableName)
              .select('geofips, year, burden, total_cost, location_name, county, state')
              .not('burden', 'is', null)
              .range(from, from + pageSize - 1);

            if (countyName) {
              query = query.ilike('county', `%${countyName}%`);
            }

            const { data, error: pageError } = await query;
            if (pageError) throw pageError;
            const pageRows = (data || []) as BurdenRow[];
            allRows.push(...pageRows);
            if (pageRows.length < pageSize) break;
            from += pageSize;
          }

          return allRows;
        };

        for (const tableName of candidateTables) {
          try {
            const countyHint =
              locationInfo?.type === 'city' || locationInfo?.type === 'county'
                ? (locationInfo.county || location).replace(/\s+County$/i, '').trim()
                : null;
            const candidateRows = await fetchBurdenRows(tableName, countyHint);
            console.log('[TransportationBurden]', tableName, { rowCount: candidateRows.length, countyHint: countyHint ?? null });
            const filteredRows = await filterRowsForLocation(candidateRows, locationInfo, location);
            if (filteredRows.length > 0) {
              rows = filteredRows;
              const statewideRows = await fetchBurdenRows(tableName, null);
              const californiaRows = await filterRowsForLocation(statewideRows, null, 'California');
              const statewidePoints = aggregateSeries(californiaRows);
              setStateSeries(statewidePoints);
              break;
            }
          } catch (supabaseError: any) {
            lastError = supabaseError?.message ?? null;
            continue;
          }
        }

        // If we got 0 rows, try without the burden filter in case every burden is null
        if (!rows && !lastError) {
          const { data: rawData, error: rawErr } = await supabaseClient
            .from('transportation_burden')
            .select('year, burden, total_cost')
            .limit(100);
          console.log('[TransportationBurden] raw (no filter)', { error: rawErr?.message ?? null, rowCount: (rawData || []).length, sample: (rawData || []).slice(0, 2) });
        }

        if (!rows) {
          setError(
            lastError ||
              'No transportation burden data found for this location. Check: (1) Row Level Security on transportation_burden — add a policy allowing SELECT for anon. (2) Table has rows with matching location/county and non-null burden.'
          );
          setLoading(false);
          return;
        }

        aggregateAndSet(rows);
      } catch (err) {
        console.error('TransportationBurdenSection:', err);
        setError(err instanceof Error ? err.message : 'Failed to load transportation burden data');
      } finally {
        setLoading(false);
      }
    };

    function normalizeCountyName(value: string | null | undefined): string {
      if (!value) return '';
      return value.toLowerCase().replace(/\s+county$/i, '').trim();
    }

    function normalizeGeofips(value: string | number | null | undefined): string {
      if (value == null) return '';
      const asNumber = Number(value);
      if (!Number.isNaN(asNumber)) return String(Math.trunc(asNumber));
      return String(value).trim();
    }

    async function getTractPlaceCrosswalkRows(tractGeofips: string[], year: number): Promise<TractPlaceCrosswalkRow[]> {
      if (tractGeofips.length === 0) return [];
      const tableName = year >= 2019 ? 'relationship_trct_place_20' : 'relationship_trct_place_10';
      const chunks: string[][] = [];
      for (let i = 0; i < tractGeofips.length; i += 500) {
        chunks.push(tractGeofips.slice(i, i + 500));
      }

      const allRows: any[] = [];
      let queryErrors: string[] = [];
      for (const chunk of chunks) {
        // Try common tract column names used across uploaded relationship tables.
        const tractColumns = ['trct', 'tract_geofips'];
        let chunkRows: any[] = [];
        let gotChunk = false;
        for (const tractCol of tractColumns) {
          const { data, error: chunkError } = await supabaseClient
            .from(tableName)
            .select('*')
            .in(tractCol, chunk);
          if (!chunkError) {
            chunkRows = (data || []) as any[];
            gotChunk = true;
            break;
          }
          queryErrors.push(chunkError.message);
        }
        if (!gotChunk) {
          throw new Error(`Failed loading ${tableName}: ${queryErrors[0] || 'unknown schema mismatch'}`);
        }
        allRows.push(...chunkRows);
      }

      const rows = allRows;
      return rows
        .map((r) => {
          const weightVal =
            r.weight ??
            (r.TRCTPOPPCT != null ? Number(r.TRCTPOPPCT) / 100 : null) ??
            (r.trctpoppct != null ? Number(r.trctpoppct) / 100 : null) ??
            (r.pop_pct != null ? Number(r.pop_pct) / 100 : null);
          return {
            trct: r.trct ?? r.tract_geofips,
            stplc: r.stplc ?? r.place_geofips,
            weight: Number(weightVal)
          };
        })
        .filter((r) => normalizeGeofips(r.trct) !== '' && normalizeGeofips(r.stplc) !== '' && Number.isFinite(r.weight) && r.weight > 0);
    }

    async function allocateTractsToPlace(rows: BurdenRow[], placeGeofips: string): Promise<BurdenRow[]> {
      const normalizedPlaceGeofips = normalizeGeofips(placeGeofips);
      const byYear = new Map<number, BurdenRow[]>();
      rows.forEach((r) => {
        const year = Number(r.year);
        if (!Number.isNaN(year)) {
          const arr = byYear.get(year) ?? [];
          arr.push(r);
          byYear.set(year, arr);
        }
      });

      const allocatedRows: BurdenRow[] = [];

      for (const [year, yearRows] of Array.from(byYear.entries())) {
        const tractIds = Array.from(new Set(yearRows.map((r) => normalizeGeofips(r.geofips)).filter(Boolean)));
        const crosswalk = await getTractPlaceCrosswalkRows(tractIds, year);
        const tractWeight = new Map<string, number>();
        crosswalk.forEach((cw) => {
          if (normalizeGeofips(cw.stplc) !== normalizedPlaceGeofips) return;
          const tract = normalizeGeofips(cw.trct);
          tractWeight.set(tract, (tractWeight.get(tract) ?? 0) + cw.weight);
        });

        yearRows.forEach((row) => {
          const tract = normalizeGeofips(row.geofips);
          const weight = tractWeight.get(tract);
          if (!weight || weight <= 0) return;
          allocatedRows.push({
            ...row,
            burden: Number(row.burden) * weight,
            total_cost: Number(row.total_cost) * weight,
            allocation_weight: weight
          });
        });
      }

      return allocatedRows;
    }

    async function filterRowsForLocation(rows: BurdenRow[], locInfo: LocationInfo | null, selectedLocation: string): Promise<BurdenRow[]> {
      if (selectedLocation.trim().toLowerCase() === 'california') {
        return rows;
      }

      // For place-level views, allocate tract rows to places using relationship_trct_place tables.
      if (locInfo?.type === 'city') {
        const placeGeofips = await supabaseLocationService.getGeofips(selectedLocation);
        if (placeGeofips) {
          const allocated = await allocateTractsToPlace(rows, String(placeGeofips));
          if (allocated.length > 0) return allocated;
        }
      }

      if (locInfo?.type === 'county') {
        const selectedCounty = normalizeCountyName(selectedLocation);
        const byName = rows.filter((r) => normalizeCountyName(r.county) === selectedCounty);
        if (byName.length > 0) return byName;
        const countyGeofips = await supabaseLocationService.getGeofips(selectedLocation);
        if (countyGeofips) {
          const countyPrefix = normalizeGeofips(String(countyGeofips));
          return rows.filter((r) => normalizeGeofips(r.geofips).startsWith(countyPrefix));
        }
        return byName;
      }

      if (locInfo?.type === 'city') {
        const selectedCity = selectedLocation.trim().toLowerCase();
        const directCityMatch = rows.filter((r) => (r.location_name || '').trim().toLowerCase() === selectedCity);
        if (directCityMatch.length > 0) return directCityMatch;

        // Fallback for datasets keyed only at county level for city selections.
        const cityCounty = normalizeCountyName(locInfo.county);
        if (cityCounty) {
          return rows.filter((r) => normalizeCountyName(r.county) === cityCounty);
        }
      }

      // Unknown location type or unmatched metadata: try direct location_name match.
      const selected = selectedLocation.trim().toLowerCase();
      return rows.filter((r) => (r.location_name || '').trim().toLowerCase() === selected);
    }

    function aggregateSeries(rows: BurdenRow[]): BurdenYearPoint[] {
      if (rows.length === 0) return [];
      const withYear = rows
        .map((r) => ({
          year: Number(r.year),
          burden: Number(r.burden),
          total_cost: Number(r.total_cost),
          weight: Number(r.allocation_weight ?? 1)
        }))
        .filter((r) => !Number.isNaN(r.year));
      if (withYear.length === 0) return [];
      const byYear = new Map<number, { burdenWeightedSum: number; burdenWeight: number; costWeightedSum: number; costWeight: number; rowCount: number }>();
      for (const row of withYear) {
        const entry = byYear.get(row.year) ?? { burdenWeightedSum: 0, burdenWeight: 0, costWeightedSum: 0, costWeight: 0, rowCount: 0 };
        if (!Number.isNaN(row.burden) && row.burden > 0 && row.burden < 10) {
          entry.burdenWeightedSum += row.burden;
          entry.burdenWeight += row.weight;
        }
        if (!Number.isNaN(row.total_cost) && row.total_cost >= 0) {
          entry.costWeightedSum += row.total_cost;
          entry.costWeight += row.weight;
        }
        entry.rowCount += 1;
        byYear.set(row.year, entry);
      }

      return Array.from(byYear.entries())
        .map(([year, values]) => ({
          year,
          avgBurdenPct: values.burdenWeight > 0 ? (values.burdenWeightedSum / values.burdenWeight) * 100 : null,
          avgMonthlyCost: values.costWeight > 0 ? values.costWeightedSum / values.costWeight : null,
          tractCount: values.rowCount
        }))
        .sort((a, b) => a.year - b.year);
    }

    function aggregateAndSet(rows: BurdenRow[]) {
      const points = aggregateSeries(rows);
      if (points.length === 0) return;
      setSeries(points);

      const latestPoint = points[points.length - 1];
      setMetrics({
        avgBurdenPct: latestPoint?.avgBurdenPct ?? null,
        avgMonthlyCost: latestPoint?.avgMonthlyCost ?? null,
        year: latestPoint?.year ?? null,
        tractCount: latestPoint?.tractCount ?? 0
      });
    }

    load();
  }, [location]);

  useEffect(() => {
    if (!onSummaryCalculated) return;
    const latestLocal = series.length > 0 ? series[series.length - 1] : null;
    if (!latestLocal) {
      onSummaryCalculated({
        locationBurdenPct: null,
        californiaBurdenPct: null,
        locationMonthlyCost: null,
        californiaMonthlyCost: null,
        year: null
      });
      return;
    }
    const latestYear = latestLocal.year;
    const stateAtYear = stateSeries.find((p) => p.year === latestYear) ?? null;
    onSummaryCalculated({
      locationBurdenPct: latestLocal.avgBurdenPct,
      californiaBurdenPct: stateAtYear?.avgBurdenPct ?? null,
      locationMonthlyCost: latestLocal.avgMonthlyCost,
      californiaMonthlyCost: stateAtYear?.avgMonthlyCost ?? null,
      year: latestYear
    });
  }, [series, stateSeries, onSummaryCalculated]);

  if (loading) {
    return <div className="placeholder-chart">Loading transportation burden data…</div>;
  }

  if (error) {
    return (
      <div className="placeholder-chart" style={{ color: '#6b7280' }}>
        {error}
      </div>
    );
  }

  return (
    <div className="transportation-burden-metrics" style={{ padding: '1rem 0' }}>
      {series.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {(() => {
            const comparisonYears = Array.from(new Set([...series.map((p) => p.year), ...stateSeries.map((p) => p.year)])).sort((a, b) => a - b);
            if (chartView === 'cost') {
              return (
                <ChartRendererSupabase
                  chartData={{
                    labels: comparisonYears,
                    datasets: [
                      {
                        label: location,
                        data: comparisonYears.map((year) => series.find((p) => p.year === year)?.avgMonthlyCost ?? null),
                        borderColor: '#059669',
                        backgroundColor: '#059669',
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        tension: 0.25,
                        spanGaps: true
                      },
                      {
                        label: 'California',
                        data: comparisonYears.map((year) => stateSeries.find((p) => p.year === year)?.avgMonthlyCost ?? null),
                        borderColor: '#f59e0b',
                        backgroundColor: '#f59e0b',
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        tension: 0.25,
                        spanGaps: true,
                        borderDash: [6, 4]
                      }
                    ]
                  }}
                  chartOptions={{
                    type: 'line',
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        title: { display: true, text: 'Year' }
                      },
                      y: {
                        type: 'linear',
                        position: 'left',
                        title: { display: true, text: 'Monthly Cost ($)' },
                        ticks: {
                          callback: (value: number | string) => `$${Number(value).toLocaleString()}`
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 18,
                          boxWidth: 14
                        }
                      },
                      title: {
                        display: true,
                        text: `Avg. monthly transportation cost: ${location} vs California`
                      }
                    }
                  }}
                  height="400px"
                  location={location}
                />
              );
            }
            return (
              <ChartRendererSupabase
                chartData={{
                  labels: comparisonYears,
                  datasets: [
                    {
                      label: location,
                      data: comparisonYears.map((year) => series.find((p) => p.year === year)?.avgBurdenPct ?? null),
                      borderColor: '#1e3a8a',
                      backgroundColor: '#1e3a8a',
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      tension: 0.25,
                      spanGaps: true,
                      yAxisID: 'y'
                    },
                    {
                      label: 'California',
                      data: comparisonYears.map((year) => stateSeries.find((p) => p.year === year)?.avgBurdenPct ?? null),
                      borderColor: '#f59e0b',
                      backgroundColor: '#f59e0b',
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      tension: 0.25,
                      spanGaps: true,
                      borderDash: [6, 4],
                      yAxisID: 'y'
                    }
                  ]
                }}
                chartOptions={{
                  type: 'line',
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      title: { display: true, text: 'Year' }
                    },
                    y: {
                      type: 'linear',
                      position: 'left',
                      title: { display: true, text: 'Burden (%)' },
                      ticks: {
                        callback: (value: number | string) => {
                          const numericValue = Number(value);
                          if (Number.isNaN(numericValue)) return `${value}%`;
                          const decimals = Math.abs(numericValue) < 1 ? 2 : 1;
                          return `${numericValue.toFixed(decimals)}%`;
                        }
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 18,
                        boxWidth: 14
                      }
                    },
                    title: {
                      display: true,
                      text: `% Income spent on transportation: ${location} vs California`
                    }
                  }
                }}
                height="400px"
                location={location}
              />
            );
          })()}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-start' }}>
        {metrics.avgBurdenPct != null && (
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a' }}>
              {(metrics.avgBurdenPct).toFixed(1)}%
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              % Income spent on transportation{metrics.year ? `, ${metrics.year}` : ''}
            </div>
          </div>
        )}
        {metrics.avgMonthlyCost != null && (
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#059669' }}>
              ${(metrics.avgMonthlyCost).toFixed(0)}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Avg. monthly transportation cost{metrics.year ? `, ${metrics.year}` : ''}
            </div>
          </div>
        )}
        {metrics.avgBurdenPct == null && metrics.avgMonthlyCost == null && !error && (
          <div style={{ color: '#6b7280' }}>No summary metrics available.</div>
        )}
      </div>
    </div>
  );
};

export default TransportationBurdenSection;
